import { create } from 'zustand';

interface AuthUser {
  id: string;
  telegramId: string;
  language: string;
  firstName: string | null;
}

interface AuthState {
  jwt: string | null;
  user: AuthUser | null;
  /** Last-known server-side onboarding step. Persisted to sessionStorage. */
  onboardingStep: number | null;
  status: 'idle' | 'authenticating' | 'authenticated' | 'error' | 'pending_deletion';
  error: string | null;
  /** P1.6 T25 — populated when /auth/verify or any authed route returns 403 account_pending_deletion. */
  pendingDeletion: { deletedAt: string; gracePeriodDays: number } | null;
  setAuth: (jwt: string, user: AuthUser) => void;
  setOnboardingStep: (step: number) => void;
  setError: (msg: string) => void;
  setAuthenticating: () => void;
  setPendingDeletion: (info: { deletedAt: string; gracePeriodDays: number }) => void;
  reset: () => void;
}

function safeGet(key: string): string | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // private-mode browsers may throw; ignore
  }
}

function safeRemove(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    // ignore
  }
}

function readStep(): number | null {
  const raw = safeGet('onboardingStep');
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isInteger(n) ? n : null;
}

export const useAuth = create<AuthState>((set) => ({
  jwt: safeGet('jwt'),
  user: null,
  onboardingStep: readStep(),
  status: 'idle',
  error: null,
  pendingDeletion: null,
  setAuth: (jwt, user) => {
    safeSet('jwt', jwt);
    set({ jwt, user, status: 'authenticated', error: null, pendingDeletion: null });
  },
  setOnboardingStep: (step) => {
    safeSet('onboardingStep', String(step));
    set({ onboardingStep: step });
  },
  setError: (msg) => set({ status: 'error', error: msg }),
  setAuthenticating: () => set({ status: 'authenticating', error: null }),
  setPendingDeletion: (info) => {
    safeRemove('jwt');
    set({ jwt: null, user: null, status: 'pending_deletion', error: null, pendingDeletion: info });
  },
  reset: () => {
    safeRemove('jwt');
    safeRemove('onboardingStep');
    set({ jwt: null, user: null, onboardingStep: null, status: 'idle', error: null, pendingDeletion: null });
  },
}));
